import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {pluralize} from 'BenchlingUtil';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {EXPERIMENT_VALIDATION_CONSTANTS} from 'studies/StudyConstants';
import ReplicateLimitLabel from 'studies/typed/shared/ReplicateLimitLabel';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import ButtonGroup from 'ui-library/buttons/ButtonGroup';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import stopPropagation from 'util/stopPropagation';

import AddConditionsModal from './AddConditionsModal';
import AddPlannedConditionsModal from './AddPlannedConditionsModal';
import {
  buildDraftConditionGroupPmdvMap,
  getConditionCountsByName,
  getDefaultConditionNames,
} from './conditionUtils';
import {ConditionGroup} from './useExperimentDesignState';
import {
  isAtPlannedConditionsLimit,
  MAX_PLAN_CONDITION_COUNT,
  MethodExecutionLimitState,
} from './useMethodExecutionLimitState';

/**
 * Callback invoked when the user picks "Add multiple …" from the dropdown. The shell hands the
 * variant its own `addConditions(numConditions, numReplicates)` so each flavor can open its
 * preferred bulk-add modal (`AddPlannedConditionsModal` for plans, `AddConditionsModal` for
 * runs) and wire the modal's submit through to this same callback.
 */
export type OpenBulkAddModal = (onSubmit: (numConditions: number, numReplicates: number) => void) => void;

export interface EditNumConditionGroupsButtonsProps {
  conditions: Array<ConditionGroup>;
  setConditions: (conditions: Array<ConditionGroup>, prevIndexes: Array<number | null>) => void;
  methodExecutionLimitState: MethodExecutionLimitState;
  procedureType: ProcedureType;
  /** Whether to render the inline `ReplicateLimitLabel` next to the buttons. */
  showReplicateLimitLabel: boolean;
  /**
   * Variant-specific: whether the at-limit state disables further adds. Plans use a
   * condition-count limit; runs use a replicate-count limit.
   */
  isAtLimit: boolean;
  /** Variant-specific: tooltip text shown when {@link isAtLimit} is true. */
  limitTooltipText: string;
  /** Variant-specific: opens the bulk-add modal. See {@link OpenBulkAddModal}. */
  openBulkAddModal: OpenBulkAddModal;
  /**
   * Optional explicit coverage for newly created draft conditions. We default to all methods if not provided.
   */
  newConditionCoveredMethodIds?: ReadonlyArray<string>;
}

/**
 * Shared shell for the add-condition buttons. Both plan and run flavors of the legacy
 * non-flowchart design tab use this shell, spreading per-flavor variant props for the
 * at-limit predicate/tooltip and the bulk-add modal. The shell owns the `addConditions`
 * book-keeping that produces new {@link ConditionGroup} lanes with `conditionIdsByPmdvId: {}`
 * — the empty-map convention the save serializers consume to detect new lanes.
 *
 * Variant differences are passed as props rather than discriminated by a flag so the shell
 * stays composable. Each flavor exports a colocated variant-helper function
 * ({@link planConditionGroupsButtonVariant}, and the run equivalent introduced alongside the
 * run-side ConditionGroup migration) that returns the three variant-specific props ready to
 * be spread into a callsite — keeping the per-flavor invocation DRY without re-introducing
 * a wrapper component layer.
 */
export function EditNumConditionGroupsButtons(props: EditNumConditionGroupsButtonsProps): JSX.Element {
  function addConditions(numConditions: number, numReplicates: number): void {
    // Generate default names for new conditions. We compute names for the full list (including
    // existing) so the index in each generated name reflects the new total, then take only the
    // new-condition tail.
    const defaultConditionsNames = getDefaultConditionNames(
      props.conditions.length + numConditions,
      props.procedureType
    );
    const existingConditionNames = props.conditions.map((condition) => condition.name);
    const newConditionNames = defaultConditionsNames.slice(existingConditionNames.length);
    const allConditionNames = existingConditionNames.concat(newConditionNames);
    const conditionCounts = getConditionCountsByName(allConditionNames);

    props.setConditions(
      [
        // Re-emit existing conditions with refreshed validation state (duplicate-name flags
        // may flip when new conditions share names with existing ones).
        ...props.conditions.map((condition) => ({
          ...condition,
          validationState: {
            ...condition.validationState,
            isDuplicateName: conditionCounts.get(condition.name)! > 1,
            isNameLengthOverLimit:
              condition.name.length > EXPERIMENT_VALIDATION_CONSTANTS.CONDITION_NAME_MAX_LENGTH,
            isDescLengthOverLimit:
              (condition.description ?? '').length >
              EXPERIMENT_VALIDATION_CONSTANTS.CONDITION_DESCRIPTION_MAX_LENGTH,
          },
        })),
        // Newly added lanes are drafts. If method coverage is provided, initialize those PMDV keys with
        // null ids (covered but unsaved); otherwise default to full-coverage empty-map behavior.
        ...Array.from(
          {length: numConditions},
          (_, index: number): ConditionGroup => ({
            conditionIdsByPmdvId: buildDraftConditionGroupPmdvMap(props.newConditionCoveredMethodIds),
            numReplicates,
            name: newConditionNames[index],
            validationState: {
              isDuplicateName: conditionCounts.get(newConditionNames[index])! > 1,
              isNameLengthOverLimit:
                newConditionNames[index].length > EXPERIMENT_VALIDATION_CONSTANTS.CONDITION_NAME_MAX_LENGTH,
              isDescLengthOverLimit: false,
            },
          })
        ),
      ],
      [...props.conditions.map((_, i) => i), ...new Array(numConditions).fill(null)]
    );
  }

  // New conditions default to the same replicate count as the last existing condition.
  const lastNumReplicates =
    props.conditions.length > 0 ? props.conditions[props.conditions.length - 1].numReplicates : 1;

  return (
    <div className={css(styles.buttonContainer)}>
      <div
        className={css(styles.button)}
        // Embedded in a table header that toggles collapse on double-click. Prevent that
        // bubbling so rapid button clicks don't accidentally collapse/expand the table.
        onDoubleClick={stopPropagation}
      >
        <Tooltip text={props.limitTooltipText} isDisabled={!props.isAtLimit}>
          <ButtonGroup>
            <Button
              buttonStyle={ButtonStyle.TERTIARY_BLUE}
              icon="compose"
              size={ButtonSize.MINI}
              onClick={() => {
                addConditions(1, lastNumReplicates);
              }}
              disabled={props.isAtLimit}
              id="add-condition-button"
            />
            <Dropdown
              menu={
                <Menu width={MenuWidth.SMALL}>
                  <Option
                    dataTestAttrs={{
                      component: 'AddConditionsDropdown',
                      element: 'addMultipleConditionsButton',
                    }}
                    onSelect={() => {
                      props.openBulkAddModal(addConditions);
                    }}
                  >
                    {`Add multiple ${getProcedureRunConditionTextForProcedureType(props.procedureType, {
                      plural: true,
                    })}`}
                  </Option>
                </Menu>
              }
            >
              <Button
                buttonStyle={ButtonStyle.TERTIARY_BLUE}
                size={ButtonSize.MINI}
                isDropdown={true}
                hideDropdownCaret={true}
                icon="chevron-down"
                iconAltText="Show dropdown menu"
                dataTestAttrs={{component: 'AddConditionsDropdown', element: 'addMultipleConditionsDropdown'}}
                disabled={props.isAtLimit}
                id="add-multiple-conditions-dropdown-button"
              />
            </Dropdown>
          </ButtonGroup>
        </Tooltip>
      </div>
      {props.showReplicateLimitLabel && (
        <ReplicateLimitLabel replicateLimit={props.methodExecutionLimitState.replicateLimit} />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  button: {paddingTop: '3px'},
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: '12px',
    marginLeft: 12,
  },
});

/**
 * Returns the plan-flavored variant props for {@link EditNumConditionGroupsButtons}: the
 * planned-condition-count limit predicate / tooltip, and a bulk-add modal call that opens
 * {@link AddPlannedConditionsModal}. Spread the result into a `<EditNumConditionGroupsButtons>`
 * callsite to render the plan flavor without an intermediate wrapper component.
 */
export function planConditionGroupsButtonVariant({
  conditions,
  methodExecutionLimitState,
  procedureType,
}: {
  conditions: ReadonlyArray<ConditionGroup>;
  methodExecutionLimitState: MethodExecutionLimitState;
  procedureType: ProcedureType;
}): Pick<EditNumConditionGroupsButtonsProps, 'isAtLimit' | 'limitTooltipText' | 'openBulkAddModal'> {
  return {
    isAtLimit: isAtPlannedConditionsLimit(methodExecutionLimitState),
    limitTooltipText: `Cannot exceed ${MAX_PLAN_CONDITION_COUNT} planned conditions`,
    openBulkAddModal: (onSubmit) => {
      legacyRunModal(AddPlannedConditionsModal, {
        onRequestSubmit: ({numConditions, numReplicates}) => {
          onSubmit(numConditions, numReplicates);
        },
        remainingConditions: MAX_PLAN_CONDITION_COUNT - conditions.length,
        procedureType,
      });
    },
  };
}

/**
 * Returns the run-flavored variant props for {@link EditNumConditionGroupsButtons}: the
 * replicate-count limit predicate / tooltip, and a bulk-add modal call that opens
 * {@link AddConditionsModal}. Spread the result into a `<EditNumConditionGroupsButtons>`
 * callsite to render the run flavor without an intermediate wrapper component.
 */
export function runConditionGroupsButtonVariant({
  conditions,
  methodExecutionLimitState,
  procedureType,
}: {
  conditions: ReadonlyArray<ConditionGroup>;
  methodExecutionLimitState: MethodExecutionLimitState;
  procedureType: ProcedureType;
}): Pick<EditNumConditionGroupsButtonsProps, 'isAtLimit' | 'limitTooltipText' | 'openBulkAddModal'> {
  const {replicateLimit, remainingReplicates} = methodExecutionLimitState;
  // Default to the same replicate count as the last existing row; forwarded to the bulk-add
  // modal as initialReplicates.
  const lastNumReplicates = conditions.length > 0 ? conditions[conditions.length - 1].numReplicates : 1;

  return {
    isAtLimit: remainingReplicates === 0,
    limitTooltipText: `Cannot exceed ${replicateLimit} ${pluralize(replicateLimit, 'replicate')}`,
    openBulkAddModal: (onSubmit) => {
      legacyRunModal(AddConditionsModal, {
        onRequestSubmit: ({numConditions, numReplicates}) => {
          onSubmit(numConditions, numReplicates);
        },
        remainingReplicates,
        replicateLimit,
        initialReplicates: lastNumReplicates,
        procedureType,
      });
    },
  };
}
